/**
 * Shared behaviour for the whole theme. Framework-free, no dependencies.
 *
 * Everything here is progressive enhancement over markup that already works
 * without it: accordions are <details>, tabs are real buttons with panels
 * that are visible when JS is absent, carousels are scroll-snap tracks that
 * scroll natively, and videos are plain <video> elements.
 *
 * Theme Editor lifecycle: every initialiser is idempotent (guarded by a
 * data flag) and re-runs scoped to the freshly re-rendered section on
 * `shopify:section:load`, because Shopify replaces a section's DOM wholesale
 * when its settings change.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ helpers -- */

  function onceInit(el, flag) {
    if (el.dataset[flag]) return false;
    el.dataset[flag] = 'true';
    return true;
  }

  function trapFocus(container, event) {
    var focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  window.themeUtils = { onceInit: onceInit, trapFocus: trapFocus };

  /* ------------------------------------------------------ sticky offset --
     Keeps --sticky-offset equal to the real height of whatever is pinned to
     the top of the viewport, so sticky product columns and scroll anchors
     never end up underneath the header. */

  function updateStickyOffset() {
    var pinned = document.querySelectorAll('[data-sticky-top]');
    var total = 0;
    pinned.forEach(function (el) {
      if (getComputedStyle(el).position === 'sticky' || el.dataset.stickyTop === 'always') {
        total += el.offsetHeight;
      }
    });
    document.documentElement.style.setProperty('--sticky-offset', total + 'px');
  }

  /* ---------------------------------------------------------- accordion --
     <details> already does the work; this only enforces "one open at a
     time" for groups that ask for it, and keeps the group height stable. */

  function initAccordions(root) {
    root.querySelectorAll('[data-accordion][data-accordion-single]').forEach(function (group) {
      if (!onceInit(group, 'accordionInit')) return;
      group.addEventListener('toggle', function (event) {
        var item = event.target;
        if (!item.matches('details') || !item.open) return;
        group.querySelectorAll('details[open]').forEach(function (other) {
          if (other !== item) other.open = false;
        });
      }, true);
    });
  }

  /* --------------------------------------------------------------- tabs --
     Roving-tabindex tablist. Panels are plain elements; without JS every
     panel is visible and the page still reads correctly top to bottom. */

  function initTabs(root) {
    root.querySelectorAll('[data-tabs]').forEach(function (group) {
      if (!onceInit(group, 'tabsInit')) return;

      var tabs = Array.prototype.slice.call(group.querySelectorAll('[role="tab"]'));
      var panels = Array.prototype.slice.call(group.querySelectorAll('[role="tabpanel"]'));
      if (!tabs.length || !panels.length) return;

      group.classList.add('is-enhanced');

      function select(index, focus) {
        tabs.forEach(function (tab, i) {
          var selected = i === index;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
        });
        panels.forEach(function (panel, i) {
          panel.hidden = i !== index;
        });
        if (focus) tabs[index].focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(i, false);
        });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next === null) return;
          event.preventDefault();
          select(next, true);
        });
      });

      var initial = tabs.findIndex(function (tab) {
        return tab.getAttribute('aria-selected') === 'true';
      });
      select(initial === -1 ? 0 : initial, false);
    });
  }

  /* ----------------------------------------------------------- carousel --
     The track scrolls natively. This wires the prev/next buttons and keeps
     their disabled state honest at either end. */

  function initCarousels(root) {
    root.querySelectorAll('[data-carousel]').forEach(function (carousel) {
      if (!onceInit(carousel, 'carouselInit')) return;

      var track = carousel.querySelector('[data-carousel-track]');
      var prev = carousel.querySelector('[data-carousel-prev]');
      var next = carousel.querySelector('[data-carousel-next]');
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function updateButtons() {
        var maxScroll = track.scrollWidth - track.clientWidth - 1;
        var scrollable = maxScroll > 0;
        if (prev) prev.disabled = !scrollable || track.scrollLeft <= 0;
        if (next) next.disabled = !scrollable || track.scrollLeft >= maxScroll;
        carousel.classList.toggle('is-static', !scrollable);
      }

      function scrollBy(direction) {
        track.scrollBy({
          left: step() * direction,
          behavior: prefersReducedMotion.matches ? 'auto' : 'smooth'
        });
      }

      if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
      if (next) next.addEventListener('click', function () { scrollBy(1); });
      track.addEventListener('scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons);
      updateButtons();
    });
  }

  /* ------------------------------------------------------ before/after --
     Draggable comparison slider. Also a real <input type="range"> so it is
     keyboard operable and announced correctly with no extra ARIA. */

  function initBeforeAfter(root) {
    root.querySelectorAll('[data-before-after]').forEach(function (widget) {
      if (!onceInit(widget, 'beforeAfterInit')) return;
      var range = widget.querySelector('[data-before-after-range]');
      if (!range) return;

      function apply() {
        widget.style.setProperty('--reveal', range.value + '%');
      }

      range.addEventListener('input', apply);
      apply();
    });
  }

  /* --------------------------------------------------------- countdown --
     Counts down to a target time. When the target has passed it re-arms on
     a repeating window (the "urgency" pattern) rather than showing a dead
     zeroed timer, and hides itself entirely if configured not to repeat. */

  function initCountdowns(root) {
    root.querySelectorAll('[data-countdown]').forEach(function (el) {
      if (!onceInit(el, 'countdownInit')) return;

      var repeatHours = parseFloat(el.dataset.countdownRepeatHours) || 0;
      var target = el.dataset.countdownTarget ? Date.parse(el.dataset.countdownTarget) : NaN;
      var units = {
        days: el.querySelector('[data-countdown-days]'),
        hours: el.querySelector('[data-countdown-hours]'),
        minutes: el.querySelector('[data-countdown-minutes]'),
        seconds: el.querySelector('[data-countdown-seconds]')
      };

      function nextTarget() {
        if (!isNaN(target)) return target;
        if (repeatHours <= 0) return NaN;
        var windowMs = repeatHours * 3600000;
        return Math.ceil(Date.now() / windowMs) * windowMs;
      }

      function pad(value) {
        return String(Math.max(0, value)).padStart(2, '0');
      }

      function tick() {
        var end = nextTarget();
        if (isNaN(end)) {
          el.hidden = true;
          return;
        }
        var remaining = end - Date.now();
        if (remaining <= 0) {
          if (repeatHours > 0) {
            target = NaN;
            remaining = nextTarget() - Date.now();
          } else {
            el.hidden = true;
            clearInterval(timer);
            return;
          }
        }
        var totalSeconds = Math.floor(remaining / 1000);
        var days = Math.floor(totalSeconds / 86400);
        var hours = Math.floor((totalSeconds % 86400) / 3600);
        if (!units.days) hours += days * 24;
        if (units.days) units.days.textContent = pad(days);
        if (units.hours) units.hours.textContent = pad(hours);
        if (units.minutes) units.minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
        if (units.seconds) units.seconds.textContent = pad(totalSeconds % 60);
      }

      tick();
      var timer = setInterval(tick, 1000);
    });
  }

  /* ---------------------------------------------------- background video --
     Decorative videos only play while on screen, and never autoplay for
     visitors who prefer reduced motion. */

  function initBackgroundVideo(root) {
    root.querySelectorAll('[data-background-video]').forEach(function (wrapper) {
      if (!onceInit(wrapper, 'bgVideoInit')) return;
      var videos = wrapper.querySelectorAll('video');
      if (!videos.length) return;

      if (prefersReducedMotion.matches) return;

      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          videos.forEach(function (video) {
            if (entry.isIntersecting) {
              var attempt = video.play();
              if (attempt && attempt.catch) attempt.catch(function () {});
            } else {
              video.pause();
            }
          });
        });
      }, { threshold: 0.25 });

      observer.observe(wrapper);
    });
  }

  /* ------------------------------------------------------ video posters --
     A poster-image play button that swaps in the real player on demand, so
     nothing heavy loads until the visitor asks for it. */

  function initVideoPosters(root) {
    root.querySelectorAll('[data-video-trigger]').forEach(function (button) {
      if (!onceInit(button, 'videoTriggerInit')) return;
      button.addEventListener('click', function () {
        var wrapper = button.closest('[data-video-wrapper]');
        if (!wrapper) return;
        var template = wrapper.querySelector('template[data-video-template]');
        if (!template) return;
        wrapper.innerHTML = '';
        wrapper.appendChild(template.content.cloneNode(true));
        var media = wrapper.querySelector('video, iframe');
        if (media && media.tagName === 'VIDEO') {
          media.controls = true;
          var attempt = media.play();
          if (attempt && attempt.catch) attempt.catch(function () {});
        }
        if (media) media.focus({ preventScroll: true });
      });
    });
  }

  /* --------------------------------------------------------- dialogs ----
     Native <dialog> gives a focus trap, Escape-to-close and a backdrop for
     free; this only wires the open/close triggers and click-outside. */

  function initDialogs(root) {
    root.querySelectorAll('[data-dialog]').forEach(function (dialog) {
      if (!onceInit(dialog, 'dialogInit')) return;
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });
      dialog.querySelectorAll('[data-dialog-close]').forEach(function (button) {
        button.addEventListener('click', function () { dialog.close(); });
      });
    });

    root.querySelectorAll('[data-dialog-open]').forEach(function (trigger) {
      if (!onceInit(trigger, 'dialogTriggerInit')) return;
      trigger.addEventListener('click', function (event) {
        var dialog = document.getElementById(trigger.dataset.dialogOpen);
        if (!dialog || typeof dialog.showModal !== 'function') return;
        event.preventDefault();
        if (!dialog.open) dialog.showModal();
      });
    });
  }

  /* ------------------------------------------------------------- init ---- */

  function init(root) {
    initAccordions(root);
    initTabs(root);
    initCarousels(root);
    initBeforeAfter(root);
    initCountdowns(root);
    initBackgroundVideo(root);
    initVideoPosters(root);
    initDialogs(root);
    updateStickyOffset();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  window.addEventListener('resize', updateStickyOffset);
  window.addEventListener('load', updateStickyOffset);
})();
